import React from 'react';
import { useNightAudit } from '../../hooks/useNightAudit';
import { ROLES } from '../../utils/rbac';
import AuditHeader from '../../components/NightAudit/AuditHeader';
import CatchupAlert from '../../components/NightAudit/CatchupAlert';
import ProgressBar from '../../components/NightAudit/ProgressBar';
import StepPanel from '../../components/NightAudit/StepPanel';
import AdminSection from '../../components/NightAudit/AdminSection';
import { usePmsDataRefresh } from '../../agent/dataSync';
import styles from './NightAuditPage.module.css';

const NightAuditPage = ({ user }) => {
  const {
    audit,
    stepInfo,
    stepData,
    steps,
    isLoading,
    isRefreshing,
    isCompleting,
    error,
    refreshData,
    completeStep,
  } = useNightAudit();

  // Check-ins, check-outs and payments by the PMS agent change the audit steps: refresh them in place.
  usePmsDataRefresh(['bookings', 'payments'], () => refreshData());

  const isAdmin = user?.role === ROLES.PROPERTYADMIN || user?.role === ROLES.SUPERADMIN;

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading night audit...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <h2>Error Loading Night Audit</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>
          <div className={styles.emptyContent}>
            <h2>No Active Audit</h2>
            <p>There is no night audit in progress for today.</p>
          </div>
        </div>

        {isAdmin && <AdminSection currentUser={user} />}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AuditHeader
        audit={audit}
        onRefresh={refreshData}
        isRefreshing={isRefreshing}
      />

      {audit.isCatchupMode && audit.missedDatesCount > 0 && (
        <CatchupAlert
          missedCount={audit.missedDatesCount}
          missedDates={audit.allMissedDates}
          currentDate={audit.auditDate}
        />
      )}

      <ProgressBar
        steps={steps}
        currentStep={audit.currentStep}
      />

      <StepPanel
        step={audit.currentStep}
        stepInfo={stepInfo}
        stepData={stepData}
        isCompleting={isCompleting}
        onComplete={completeStep}
        onRefresh={refreshData}
      />

      {isAdmin && <AdminSection currentUser={user} />}
    </div>
  );
};

export default NightAuditPage;
